import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * FT-3a — pending FRS edits. Edits are NOT autosaved: they sit here until the foreman presses
 * Save, which sends them as ONE batch. They survive a refresh and a lost signal because they
 * live on the device until that batch is accepted.
 */
const keyFor = (projectId) => `frs-pending-${projectId}`;

const read = (projectId) => {
  try {
    return JSON.parse(localStorage.getItem(keyFor(projectId)) || "{}") || {};
  } catch {
    return {};
  }
};

export function usePendingEdits(projectId, enabled = true) {
  const [edits, setEditsState] = useState(() => (projectId ? read(projectId) : {}));
  const editsRef = useRef(edits);
  const undoStack = useRef([]);
  const [undoDepth, setUndoDepth] = useState(0);
  const setEdits = useCallback((next) => { editsRef.current = next; setEditsState(next); }, []);
  const resetUndo = useCallback(() => { undoStack.current = []; setUndoDepth(0); }, []);

  useEffect(() => { setEdits(projectId ? read(projectId) : {}); resetUndo(); }, [projectId, setEdits, resetUndo]);

  useEffect(() => {
    if (!projectId) return;
    if (Object.keys(edits).length) localStorage.setItem(keyFor(projectId), JSON.stringify(edits));
    else localStorage.removeItem(keyFor(projectId));
  }, [projectId, edits]);

  const stage = useCallback((key, entry) => {
    const cur = editsRef.current;
    // Staging the value it already has is not an edit — it clears the pending mark instead.
    if (entry === null || entry === undefined) {
      if (!(key in cur)) return;
      const { [key]: _drop, ...rest } = cur;
      undoStack.current.push({ key, prev: cur[key] });
      setEdits(rest);
    } else {
      undoStack.current.push({ key, prev: cur[key] });
      setEdits({ ...cur, [key]: { ...entry, key, at: new Date().toISOString() } });
    }
    setUndoDepth(undoStack.current.length);
  }, [setEdits]);

  // Steps back the last staged change (cell by cell), newest first.
  const undo = useCallback(() => {
    const last = undoStack.current.pop();
    setUndoDepth(undoStack.current.length);
    if (!last) return null;
    const cur = editsRef.current;
    if (last.prev) setEdits({ ...cur, [last.key]: last.prev });
    else { const { [last.key]: _d, ...rest } = cur; setEdits(rest); }
    return last;
  }, [setEdits]);

  const drop = useCallback((key) => stage(key, null), [stage]);
  const discard = useCallback(() => { setEdits({}); resetUndo(); }, [setEdits, resetUndo]);

  const list = useMemo(
    () => Object.values(edits).sort((a, b) => (a.at || "").localeCompare(b.at || "")),
    [edits],
  );

  /** "Room 110: XYZ 2 → 6" grouped by room, in the order they were made. */
  const summary = useMemo(() => {
    const byRoom = new Map();
    for (const e of list) {
      const room = e.room_label || "Job";
      if (!byRoom.has(room)) byRoom.set(room, []);
      byRoom.get(room).push(e.label);
    }
    return [...byRoom.entries()].map(([room, lines]) => ({ room, lines }));
  }, [list]);

  const ops = useMemo(() => list.map((e) => e.op), [list]);

  return { edits: enabled ? edits : {}, list, ops, summary, stage, drop, discard, undo,
           canUndo: enabled && undoDepth > 0,
           count: list.length, has: (key) => key in edits, get: (key) => edits[key] };
}

/** Does this project have edits waiting, without mounting the grid? */
export const hasPendingEdits = (projectId) => Object.keys(read(projectId)).length > 0;
